import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Table, Loader2, AlertCircle, Info } from 'lucide-react';
import axios from 'axios';

const API_URL = '/api/tarifas/comisiones-easy/matriz';

export default function ComisionesEasyMatriz() {
  const [tarifaSeleccionada, setTarifaSeleccionada] = useState(null);

  // Query para obtener las comisiones en formato matriz
  const { data, isLoading, error } = useQuery({
    queryKey: ['comisiones-easy-matriz'],
    queryFn: async () => {
      const response = await axios.get(API_URL);
      return response.data;
    },
  });

  // Seleccionar primera tarifa por defecto cuando cargan los datos
  if (data && !tarifaSeleccionada && data.metadata.tarifas.length > 0) {
    setTarifaSeleccionada(data.metadata.tarifas[0]);
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <span className="ml-3 text-gray-600">Cargando comisiones EASY...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center space-x-3 text-red-600">
          <AlertCircle className="w-6 h-6 flex-shrink-0" />
          <div>
            <p className="font-medium">Error al cargar comisiones EASY</p>
            <p className="text-sm text-red-500 mt-1">
              {error.response?.data?.error || error.message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.matrices || Object.keys(data.matrices).length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center space-x-3 text-yellow-600">
          <AlertCircle className="w-6 h-6 flex-shrink-0" />
          <div>
            <p className="font-medium">No hay comisiones EASY cargadas</p>
            <p className="text-sm text-gray-600 mt-1">
              Ejecuta el script de carga: <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                python backend/scripts/cargar_comisiones_easy.py
              </code>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const matriz = data.matrices[tarifaSeleccionada];

  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Table className="w-6 h-6 text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Comisiones EASY - Vista Matriz
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {data.metadata.total_registros} registros cargados
              </p>
            </div>
          </div>

          {/* Selector de Tarifa */}
          <select
            value={tarifaSeleccionada}
            onChange={(e) => setTarifaSeleccionada(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
          >
            {data.metadata.tarifas.map((tarifa) => (
              <option key={tarifa} value={tarifa}>
                Tarifa {tarifa}
              </option>
            ))}
          </select>
        </div>

        {/* Info box */}
        <div className="mt-4 flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-900">¿Cómo leer esta matriz?</p>
            <p className="text-blue-700 mt-1">
              Cada fila representa un rango de consumo anual (en MWh). Las columnas muestran
              las comisiones (€/CUPS/año) para cada combinación de <strong>Tipo EASY</strong>{' '}
              (BOE, 3, 6) y <strong>Nivel</strong> (5, 15, 25 €/MWh).
            </p>
          </div>
        </div>
      </div>

      {/* Tabla Matriz */}
      <div className="p-6 overflow-x-auto">
        {matriz && (
          <table className="w-full border-collapse">
            <thead>
              {/* Fila 1: Tipo EASY */}
              <tr className="bg-gray-100">
                <th
                  className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700 sticky left-0 bg-gray-100 z-10"
                  rowSpan={2}
                >
                  Consumo Anual (MWh)
                </th>
                {['BOE', '3', '6'].map((tipo) => (
                  <th
                    key={tipo}
                    colSpan={3}
                    className="border border-gray-300 px-4 py-2 text-center font-semibold text-gray-700 bg-primary-50"
                  >
                    EASY {tipo}
                  </th>
                ))}
              </tr>

              {/* Fila 2: Niveles */}
              <tr className="bg-gray-50">
                {['BOE', '3', '6'].map((tipo) =>
                  ['5', '15', '25'].map((nivel) => (
                    <th
                      key={`${tipo}_${nivel}`}
                      className="border border-gray-300 px-3 py-2 text-center text-sm font-medium text-gray-600"
                    >
                      N{nivel}
                    </th>
                  ))
                )}
              </tr>
            </thead>

            <tbody>
              {matriz.filas.map((fila, idx) => (
                <tr
                  key={idx}
                  className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                >
                  {/* Columna de rango */}
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900 sticky left-0 bg-inherit z-10">
                    {fila.rango.label}
                  </td>

                  {/* Valores de comisión */}
                  {matriz.columnas.map((col) => {
                    const valor = fila.valores[col.label];
                    return (
                      <td
                        key={col.label}
                        className="border border-gray-300 px-3 py-3 text-right text-sm"
                      >
                        {valor !== null ? (
                          <span className="font-mono text-gray-900">
                            {valor.toFixed(2)} €
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">N/A</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer con leyenda */}
      <div className="px-6 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="p-3 bg-purple-50 rounded-lg">
            <p className="font-medium text-purple-900">Tipo EASY</p>
            <ul className="mt-2 space-y-1 text-purple-700">
              <li><strong>BOE:</strong> Potencia según BOE</li>
              <li><strong>3:</strong> Fijo 3€/kW·año</li>
              <li><strong>6:</strong> Fijo 6€/kW·año</li>
            </ul>
          </div>

          <div className="p-3 bg-green-50 rounded-lg">
            <p className="font-medium text-green-900">Niveles</p>
            <ul className="mt-2 space-y-1 text-green-700">
              <li><strong>N5:</strong> 5 €/MWh en energía</li>
              <li><strong>N15:</strong> 15 €/MWh en energía</li>
              <li><strong>N25:</strong> 25 €/MWh en energía</li>
            </ul>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="font-medium text-blue-900">Valores mostrados</p>
            <p className="mt-2 text-blue-700">
              Las comisiones son <strong>€/CUPS/año</strong>, independientes del
              extramargen y potencia contratada.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
